"use client";
import { useEffect, useState } from "react";
import type {
  DesignAgentGuidance,
  DesignAgentGuidanceResource,
} from "@open-design/contracts";
import { platform } from "./api";
import { Select } from "./ui";
import css from "./BuildyWorkspace.module.css";

export function WebsiteAgentGuidance({
  websiteId,
  org,
}: {
  websiteId: string;
  org: number;
}) {
  const [data, setData] = useState<DesignAgentGuidance | null>(null);
  const [selected, setSelected] = useState("");
  const [resource, setResource] = useState<DesignAgentGuidanceResource | null>(
    null,
  );
  const [open, setOpen] = useState(false);
  const [error, setError] = useState("");
  const path = `websites/${websiteId}/agent-guidance/`;
  useEffect(() => {
    if (!open) return;
    const controller = new AbortController();
    setError("");
    setResource(null);
    void platform<{
      result: DesignAgentGuidance | DesignAgentGuidanceResource;
    }>(path + (selected ? `?resource=${encodeURIComponent(selected)}` : ""), {
      org,
      signal: controller.signal,
    })
      .then(({ result }) => {
        if (controller.signal.aborted) return;
        if (selected) setResource(result as DesignAgentGuidanceResource);
        else setData(result as DesignAgentGuidance);
      })
      .catch((e) => {
        if (!controller.signal.aborted) setError(e.message);
      });
    return () => controller.abort();
  }, [open, selected, path, org]);
  return (
    <details
      className={css.agentGuidance}
      onToggle={(event) => {
        if (event.currentTarget.open) {
          setSelected("");
          setData(null);
          setResource(null);
        }
        setOpen(event.currentTarget.open);
      }}
    >
      <summary>Shared guidance</summary>
      <p>
        Your agent receives this website's design context, the enabled Website
        Playbook and shared design skills. Each checkout keeps its own snapshot.
      </p>
      {error && <p role="alert">{error}</p>}
      {open && !data && !error && <p role="status">Loading guidance…</p>}
      {data && (
        <>
          <p>
            Source version {data.source_version} ·{" "}
            {data.resources.filter((item) => item.kind === "principle").length}{" "}
            principles ·{" "}
            {
              data.resources.filter((item) => item.id.endsWith("/SKILL.md"))
                .length
            }{" "}
            complete skills
          </p>
          <label>
            Reference
            <Select
              value={selected}
              onChange={(e) => setSelected(e.target.value)}
            >
              <option value="">Website Playbook overview</option>
              {data.resources.map((item) => (
                <option value={item.id} key={item.id}>
                  {item.id === "skills/frontend-design/SKILL.md"
                    ? "Frontend design"
                    : item.id === "skills/reference-design-contract/SKILL.md"
                      ? "Reference design contract"
                      : item.title}
                </option>
              ))}
            </Select>
          </label>
          <pre
            className={css.agentGuidanceText}
            tabIndex={0}
            aria-label="Reference content"
          >
            {selected
              ? (resource?.content?.replace(
                  /^---\r?\n[\s\S]*?\r?\n---\r?\n/,
                  "",
                ) ?? (error ? "Reference unavailable." : "Loading reference…"))
              : data.playbook_prompt}
          </pre>
          {!!data.gaps.length && (
            <>
              <strong>Missing context</strong>
              <ul>
                {data.gaps.map((gap) => (
                  <li key={gap}>{gap}</li>
                ))}
              </ul>
            </>
          )}
          <p>
            Image generation requires your agent's own tool or explicit cloud
            delegation. Playbook image attachments are not included in this text
            bundle.
          </p>
        </>
      )}
    </details>
  );
}
